//
// Copyright (c) 2023 Emplifi, Inc.
//

import type {
  Editor,
  ModelDocumentFragment,
  ModelDocumentSelection,
  ModelElement,
  ModelPositionOffset,
  ModelWriter,
  ViewDocumentSelection,
  ViewElement,
} from "ckeditor5";

export const ACCORDION = "accordion";
export const ACCORDION_SECTION_BODY = "accordionSectionBody";
export const ACCORDION_SECTION_HEADER = "accordionSectionHeader";

export type AccordionSectionType = "accordionSectionBody" | "accordionSectionHeader";

export function createAccordionSection(
  editor: Editor,
  writer: ModelWriter,
  accordion: ModelElement,
  accordionOffset?: ModelPositionOffset,
) {
  const accordionSectionHeader = writer.createElement(ACCORDION_SECTION_HEADER);
  const accordionSectionBody = writer.createElement(ACCORDION_SECTION_BODY);
  // To work around a CKEditor 5 bug, must append a paragraph to the Accordion Section Body.
  // If it is not included, the body will not be able to be edited by the user.
  const accordionBodyParagraph = writer.createElement("paragraph");
  if (accordionOffset === undefined || accordionOffset == "end") {
    writer.append(accordionSectionHeader, accordion);
    writer.append(accordionSectionBody, accordion);
  } else {
    // These are purposely reversed, and will result in the header being inserted before the body
    writer.insert(accordionSectionBody, accordion, accordionOffset);
    writer.insert(accordionSectionHeader, accordion, accordionOffset);
  }
  writer.appendText(
    editor.config.get("accordion.headerPlaceholder") as string,
    accordionSectionHeader,
  );
  writer.append(accordionBodyParagraph, accordionSectionBody);
  writer.appendText(
    editor.config.get("accordion.bodyPlaceholder") as string,
    accordionBodyParagraph,
  );
}

export function getAccordionElement(selection: ModelDocumentSelection): ModelElement | null {
  const selectionPosition = selection.getFirstPosition();
  if (!selectionPosition) {
    return null;
  }
  const selectedElement = selection.getSelectedElement();
  if (selectedElement && selectedElement.name == ACCORDION) {
    return selectedElement;
  }
  return selectionPosition.findAncestor(ACCORDION);
}

export function getAccordionSectionElement(
  selection: ModelDocumentSelection,
  sectionType?: AccordionSectionType | undefined,
): ModelElement | null {
  const selectionPosition = selection.getFirstPosition();
  if (!selectionPosition) {
    return null;
  }
  const selectedElement = selection.getSelectedElement();
  if (selectedElement) {
    const sectionElement = _getAccordionSectionElementFromElement(selectedElement, sectionType);
    if (sectionElement) {
      return sectionElement;
    }
  }
  const selectionParent = selectionPosition.parent;
  if (selectionParent.is("element")) {
    let node: ModelElement | ModelDocumentFragment | null = selectionParent;
    while (node) {
      if (node instanceof Element && node.is("element")) {
        const sectionElement = _getAccordionSectionElementFromElement(node, sectionType);
        if (sectionElement) {
          return sectionElement;
        }
        node = node.parent;
      } else {
        node = null;
      }
    }
  }
  const ancestor =
    selectionPosition.findAncestor(ACCORDION_SECTION_HEADER) ||
    selectionPosition.findAncestor(ACCORDION_SECTION_BODY);
  if (ancestor) {
    return _getAccordionSectionElementFromElement(ancestor, sectionType);
  }
  return null;
}

export function getAccordionSectionWidget(selection: ViewDocumentSelection): ViewElement | null {
  const selectionPosition = selection.getFirstPosition();
  if (!selectionPosition) {
    return null;
  }
  const selectedElement = selection.getSelectedElement();
  if (selectedElement && isAccordionSectionWidget(selectedElement)) {
    return selectedElement;
  }
  for (const node of selectionPosition.getAncestors()) {
    if (node.is("element") && isAccordionSectionWidget(node)) {
      return node;
    }
  }
  return null;
}

export function isAccordionSectionWidget(element: ViewElement) {
  return !!(
    element.getCustomProperty(ACCORDION_SECTION_HEADER) ||
    element.getCustomProperty(ACCORDION_SECTION_BODY)
  );
}

function _getAccordionSectionElementFromElement(
  element: ModelElement,
  sectionType?: AccordionSectionType | undefined,
): ModelElement | null {
  if (element.name == ACCORDION_SECTION_HEADER || element.name == ACCORDION_SECTION_BODY) {
    if (sectionType) {
      if (sectionType === element.name) {
        return element;
      }
      if (sectionType === ACCORDION_SECTION_HEADER) {
        const sibling = element.previousSibling;
        if (sibling?.is("element") && sibling.name == ACCORDION_SECTION_HEADER) {
          return sibling;
        }
        return null;
      } else if (sectionType == ACCORDION_SECTION_BODY) {
        const sibling = element.nextSibling;
        if (sibling?.is("element") && sibling.name == ACCORDION_SECTION_BODY) {
          return sibling;
        }
        return null;
      }
    } else {
      return element;
    }
  }
  return null;
}
